import requests from "../requests";
import { Dispatch } from "redux";

export enum FeatureConstants {
  FEATURED_REQUEST = "FEATURED_REQUEST",
  FEATURED_SUCCESS = "FEATURED_SUCCESS",
  FEATURED_FAILURE = "FEATURED_FAILURE",
}

// Browse-time filters mirroring /api/featured's query params (backend PR.1).
// Optional and additive — omitting this object entirely reproduces the old
// unfiltered call, so every existing getFeaturedMatches() call site keeps working.
export interface FeaturedFilters {
  direction?: "up" | "down";
  minConviction?: number;
  sort?: "conviction" | "count" | "avg_move";
  minPrice?: number;
  maxPrice?: number;
  rsiMin?: number;
  rsiMax?: number;
}

const buildFilterQuery = (filters?: FeaturedFilters): string => {
  if (!filters) return "";
  const params: Record<string, string> = {};
  if (filters.direction) params.direction = filters.direction;
  if (filters.minConviction !== undefined) params.min_conviction = String(filters.minConviction);
  if (filters.sort) params.sort = filters.sort;
  if (filters.minPrice !== undefined) params.min_price = String(filters.minPrice);
  if (filters.maxPrice !== undefined) params.max_price = String(filters.maxPrice);
  if (filters.rsiMin !== undefined) params.rsi_min = String(filters.rsiMin);
  if (filters.rsiMax !== undefined) params.rsi_max = String(filters.rsiMax);
  const qs = new URLSearchParams(params).toString();
  return qs ? `&${qs}` : "";
};

const featureService = {
  // unlock: a signed per-signal token from a social share link (?unlock=). Forwarded
  // so the backend overlays that one signal in full even when its window is Pro-locked.
  async getFeatured (date = "", unlock = "", filters?: FeaturedFilters): Promise<RootStockData> {
    const resp = await requests.get(
      `/api/featured?date=${date}${unlock ? `&unlock=${encodeURIComponent(unlock)}` : ""}${buildFilterQuery(filters)}`,
    );
    return resp.data;
  },
};

export function getFeaturedMatches(date?: string, unlock?: string, filters?: FeaturedFilters) {
  return async (dispatch: Dispatch<FeatureAction>): Promise<void> => {
    const request = (): FeatureDispatchType => ({ type: FeatureConstants.FEATURED_REQUEST });
    const success = (payload: RootStockData): FeatureDispatch => ({
      type: FeatureConstants.FEATURED_SUCCESS,
      payload,
    });
    const failure = (error: string): FeatureErrorDispatchType => ({
      type: FeatureConstants.FEATURED_FAILURE,
      error,
    });

    dispatch(request());
    try {
      const content = await featureService.getFeatured(date, unlock, filters);
      dispatch(success(content));
    } catch (error: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch(failure((error as any).response.data.error));
    }
  };
}

interface GoViewFeatured {
  pathname: string
  search: string
}

export const goViewFeatured = (direction: Direction, dataIndex: number, window: number): GoViewFeatured => ({
  pathname: "/view-featured",
  search: `?direction=${direction}&dataIndex=${dataIndex}&window=${window}`,
});
